import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase';
import LoadingSpinner from '../components/LoadingSpinner';

const TeamCompanies = () => {
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    industry: 'all',
    search: '',
    contactStatus: 'all'
  });
  const [stats, setStats] = useState({
    totalCompanies: 0,
    verifiedCompanies: 0,
    pendingCompanies: 0,
    totalJobs: 0
  });

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    filterCompanies();
  }, [companies, filters]);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const companiesSnapshot = await getDocs(collection(db, 'companies'));
      const jobsSnapshot = await getDocs(collection(db, 'jobs'));

      const companiesData = companiesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Count jobs per company - use userId as companyId
      const jobCounts = {};
      jobsSnapshot.docs.forEach(jobDoc => {
        const job = jobDoc.data();
        // Jobs are linked to companies via userId field
        const companyKey = job.companyId || job.userId;
        if (companyKey) {
          jobCounts[companyKey] = (jobCounts[companyKey] || 0) + 1;
        }
      });

      console.log('Job counts by company:', jobCounts);

      // Add job counts to companies - check both company.id and company.userId
      const companiesWithJobCounts = companiesData.map(company => {
        const count = jobCounts[company.id] || jobCounts[company.userId] || 0;
        console.log(`Company ${company.companyName || company.name}: ${count} jobs`);
        return {
          ...company,
          jobCount: count
        };
      });

      setCompanies(companiesWithJobCounts);

      // Calculate stats
      const totalCompanies = companiesWithJobCounts.length;
      const verifiedCompanies = companiesWithJobCounts.filter(company => company.isVerified).length;
      const pendingCompanies = companiesWithJobCounts.filter(company => !company.isVerified).length;
      const totalJobs = jobsSnapshot.size;

      setStats({
        totalCompanies,
        verifiedCompanies,
        pendingCompanies,
        totalJobs
      });

    } catch (error) {
      console.error('Error fetching companies:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterCompanies = () => {
    let filtered = [...companies];

    if (filters.status !== 'all') {
      if (filters.status === 'verified') {
        filtered = filtered.filter(company => company.isVerified);
      } else if (filters.status === 'pending') {
        filtered = filtered.filter(company => !company.isVerified);
      }
    }

    if (filters.industry !== 'all') {
      filtered = filtered.filter(company => company.industry === filters.industry);
    }

    // Contact status filter
    if (filters.contactStatus === 'complete') {
      filtered = filtered.filter(company => company.website && (company.phone || company.contactNumber) && (company.city || company.state || company.country || company.location));
    } else if (filters.contactStatus === 'website_only') {
      filtered = filtered.filter(company => company.website && !(company.phone || company.contactNumber));
    } else if (filters.contactStatus === 'phone_only') {
      filtered = filtered.filter(company => (company.phone || company.contactNumber) && !company.website);
    } else if (filters.contactStatus === 'location_only') {
      filtered = filtered.filter(company => (company.city || company.state || company.country || company.location) && !company.website && !(company.phone || company.contactNumber));
    } else if (filters.contactStatus === 'incomplete') {
      filtered = filtered.filter(company => !company.website && !(company.phone || company.contactNumber) && !(company.city || company.state || company.country || company.location));
    }

    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(company => {
        const locationText = `${company.city || ''} ${company.state || ''} ${company.country || ''} ${company.location || ''}`.toLowerCase();

        return company.name?.toLowerCase().includes(searchTerm) ||
               company.companyName?.toLowerCase().includes(searchTerm) ||
               company.industry?.toLowerCase().includes(searchTerm) ||
               company.website?.toLowerCase().includes(searchTerm) ||
               (company.phone || company.contactNumber)?.toLowerCase().includes(searchTerm) ||
               locationText.includes(searchTerm);
      });
    }

    setFilteredCompanies(filtered);
  };

  const handleCompanyClick = (company) => {
    setSelectedCompany(company);
    setShowCompanyModal(true);
  };

  const exportCompanies = () => {
    if (filteredCompanies.length === 0) return;

    const escapeCsvField = (field) => {
      if (field === null || field === undefined) return '';
      const stringField = String(field);
      if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\n')) {
        return `"${stringField.replace(/"/g, '""')}"`;
      }
      return stringField;
    };

    const csvContent = [
      [
        'Company Name',
        'Industry',
        'Company Size',
        'Website',
        'Phone',
        'Email',
        'City',
        'State',
        'Country',
        'Full Location',
        'Verified Status',
        'Registration Date',
        'Active Jobs',
        'Company Type',
        'Founded Year',
        'Description',
        'Company ID',
        'User ID'
      ],
      ...filteredCompanies.map(company => {
        const fullLocation = company.city || company.state || company.country
          ? `${company.city || ''}, ${company.state || ''}, ${company.country || ''}`.replace(/^,\s*|,\s*$/g, '').replace(/,\s*,/g, ',')
          : company.location || '';

        return [
          company.companyName || company.name || '',
          company.industry || '',
          company.companySize || '',
          company.website || '',
          company.phone || company.contactNumber || '',
          company.email || '',
          company.city || '',
          company.state || '',
          company.country || '',
          fullLocation,
          company.isVerified ? 'Verified' : 'Pending',
          formatDate(company.createdAt),
          company.jobCount || 0,
          company.companyType || '',
          company.foundedYear || '',
          company.description || '',
          company.id || '',
          company.userId || ''
        ].map(escapeCsvField);
      })
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `companies_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString();
  };

  const getUniqueIndustries = () => {
    const industries = companies.map(company => company.industry).filter(Boolean);
    return [...new Set(industries)];
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Company Contact Management</h1>
        <p className="text-gray-600">Manage company contacts and communication channels for effective backend support</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
          <div className="flex items-center">
            <div className="p-3 bg-blue-500 rounded-lg">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Companies</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalCompanies}</p>
            </div>
          </div>
        </div>

        {/* <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
          <div className="flex items-center">
            <div className="p-3 bg-green-500 rounded-lg">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Verified</p>
              <p className="text-2xl font-bold text-gray-900">{stats.verifiedCompanies}</p>
            </div>
          </div>
        </div> */}

        {/* <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-6 border border-yellow-200">
          <div className="flex items-center">
            <div className="p-3 bg-yellow-500 rounded-lg">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-gray-900">{stats.pendingCompanies}</p>
            </div>
          </div>
        </div> */}

        <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
          <div className="flex items-center">
            <div className="p-3 bg-purple-500 rounded-lg">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Jobs</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalJobs}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search Companies</label>
            <input
              type="text"
              placeholder="Search by name, industry, website, phone, location..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              <option value="verified">Verified</option>
              <option value="pending">Pending</option>
            </select>
          </div> */}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Industry</label>
            <select
              value={filters.industry}
              onChange={(e) => setFilters(prev => ({ ...prev, industry: e.target.value }))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Industries</option>
              {getUniqueIndustries().map(industry => (
                <option key={industry} value={industry}>{industry}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Contact Status</label>
            <select
              value={filters.contactStatus}
              onChange={(e) => setFilters(prev => ({ ...prev, contactStatus: e.target.value }))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Contacts</option>
              <option value="complete">Complete (Website + Phone + Location)</option>
              <option value="website_only">Website Only</option>
              <option value="phone_only">Phone Only</option>
              <option value="location_only">Location Only</option>
              <option value="incomplete">Incomplete</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => setFilters({ status: 'all', industry: 'all', search: '', contactStatus: 'all' })}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Companies List */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              Company Directory ({filteredCompanies.length})
            </h2>
            <p className="text-gray-600 text-sm mt-1">Manage and communicate with platform companies</p>
          </div>
          {filteredCompanies.length > 0 && (
            <button
              onClick={exportCompanies}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm font-medium flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export CSV
            </button>
          )}
        </div>

        {filteredCompanies.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {filteredCompanies.map((company) => (
              <div
                key={company.id}
                className="p-6 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {/* Company Header */}
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-14 h-14 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-lg">
                        {(company.companyName || company.name)?.charAt(0)?.toUpperCase() || 'C'}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900 mb-1">
                          {company.companyName || company.name}
                        </h3>
                        <div className="flex items-center gap-3">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                            company.isVerified
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {company.isVerified ? 'Verified' : 'Pending'}
                          </span>
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                            {company.industry || 'Technology'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Contact Status Indicators */}
                    <div className="flex items-center gap-4 mb-4">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700">Contact Quality:</span>
                        <div className="flex items-center gap-1">
                          {company.website && <span className="w-2 h-2 bg-blue-500 rounded-full" title="Website available"></span>}
                          {(company.phone || company.contactNumber) && <span className="w-2 h-2 bg-green-500 rounded-full" title="Phone available"></span>}
                          {(company.city || company.state || company.country || company.location) && <span className="w-2 h-2 bg-purple-500 rounded-full" title="Location available"></span>}
                          {!company.website && !(company.phone || company.contactNumber) && !(company.city || company.state || company.country || company.location) && <span className="w-2 h-2 bg-red-500 rounded-full" title="No contact info"></span>}
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          (company.website && (company.phone || company.contactNumber) && (company.city || company.state || company.country || company.location)) ? 'bg-green-100 text-green-800' :
                          (company.website || (company.phone || company.contactNumber) || (company.city || company.state || company.country || company.location)) ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {(company.website && (company.phone || company.contactNumber) && (company.city || company.state || company.country || company.location)) ? 'Complete' :
                           (company.website || (company.phone || company.contactNumber) || (company.city || company.state || company.country || company.location)) ? 'Partial' : 'Missing'}
                        </span>
                      </div>
                    </div>

                    {/* Contact Information - Prominent Display */}
                    <div className="bg-gray-50 rounded-xl p-6 mb-4">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <svg className="w-5 h-5 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                        Contact Information
                      </h4>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Website */}
                        <div className="bg-white rounded-lg p-4 border border-gray-200">
                          <div className="flex items-center mb-2">
                            <svg className="w-5 h-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                            </svg>
                            <span className="text-sm font-medium text-gray-700">Website</span>
                          </div>
                          <p className="text-base font-semibold text-gray-900 mb-3">{company.website || 'Not provided'}</p>
                          {company.website ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(company.website, '_blank');
                              }}
                              className="w-full px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                              </svg>
                              Visit Website
                            </button>
                          ) : (
                            <button
                              disabled
                              className="w-full px-3 py-2 bg-gray-300 text-gray-500 text-sm font-medium rounded-lg cursor-not-allowed flex items-center justify-center gap-2"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                              </svg>
                              No Website
                            </button>
                          )}
                        </div>

                        {/* Phone */}
                        <div className="bg-white rounded-lg p-4 border border-gray-200">
                          <div className="flex items-center mb-2">
                            <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            <span className="text-sm font-medium text-gray-700">Phone</span>
                          </div>
                          <p className="text-base font-semibold text-gray-900 mb-3">{company.phone || company.contactNumber || 'Not provided'}</p>
                          {(company.phone || company.contactNumber) ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(`tel:${company.phone || company.contactNumber}`, '_blank');
                              }}
                              className="w-full px-3 py-2 bg-green-500 hover:bg-green-600 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                              </svg>
                              Call Now
                            </button>
                          ) : (
                            <button
                              disabled
                              className="w-full px-3 py-2 bg-gray-300 text-gray-500 text-sm font-medium rounded-lg cursor-not-allowed flex items-center justify-center gap-2"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                              </svg>
                              No Phone
                            </button>
                          )}
                        </div>

                        {/* Location */}
                        <div className="bg-white rounded-lg p-4 border border-gray-200">
                          <div className="flex items-center mb-2">
                            <svg className="w-5 h-5 text-purple-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span className="text-sm font-medium text-gray-700">Location</span>
                          </div>
                          <p className="text-base font-semibold text-gray-900 mb-3 min-h-[3rem]">
                            {company.city || company.state || company.country
                              ? `${company.city || ''}, ${company.state || ''}, ${company.country || ''}`.replace(/^,\s*|,\s*$/g, '').replace(/,\s*,/g, ',')
                              : company.location || 'Not provided'
                            }
                          </p>
                          {(company.city || company.state || company.country || company.location) ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const location = company.city || company.state || company.country || company.location;
                                window.open(`https://maps.google.com/maps?q=${encodeURIComponent(location)}`, '_blank');
                              }}
                              className="w-full px-3 py-2 bg-purple-500 hover:bg-purple-600 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                              View on Map
                            </button>
                          ) : (
                            <button
                              disabled
                              className="w-full px-3 py-2 bg-gray-300 text-gray-500 text-sm font-medium rounded-lg cursor-not-allowed flex items-center justify-center gap-2"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                              No Location
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Additional Actions and Info */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span>Registered: {formatDate(company.createdAt)}</span>
                        <span>•</span>
                        <span>{company.jobCount} Active Jobs</span>
                        <span>•</span>
                        <span>Size: {company.companySize || 'Not specified'}</span>
                      </div>

                      {/* Quick Actions */}
                      <div className="flex items-center gap-2">
                        {(company.phone || company.contactNumber) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(`tel:${company.phone || company.contactNumber}`, '_blank');
                            }}
                            className="p-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg transition-colors"
                            title="Call"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                          </button>
                        )}
                        {company.website && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(company.website, '_blank');
                            }}
                            className="p-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg transition-colors"
                            title="Website"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                            </svg>
                          </button>
                        )}
                        {(company.phone || company.contactNumber) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(`https://wa.me/${(company.phone || company.contactNumber)?.replace(/\D/g, '')}`, '_blank');
                            }}
                            className="p-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg transition-colors"
                            title="WhatsApp"
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                            </svg>
                          </button>
                        )}
                        <button
                          onClick={() => handleCompanyClick(company)}
                          className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                          title="View Details"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-12 text-center">
            <div className="text-gray-400 text-6xl mb-4">🏢</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No companies found</h3>
            <p className="text-gray-500">No companies match your current filters.</p>
          </div>
        )}
      </div>

      {/* Company Detail Modal */}
      {showCompanyModal && selectedCompany && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl max-w-6xl w-full max-h-[95vh] overflow-hidden shadow-2xl border border-gray-100">
            {/* Header with gradient background */}
            <div className="relative bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 p-8">
              <div className="absolute inset-0 bg-black/10"></div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-6">
                  {/* Company Logo or Initial */}
                  <div className="relative">
                    {selectedCompany.logoUrl ? (
                      <img
                        src={selectedCompany.logoUrl}
                        alt={selectedCompany.companyName || selectedCompany.name}
                        className="w-20 h-20 rounded-2xl object-cover border-4 border-white/20 shadow-lg"
                      />
                    ) : (
                      <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center text-white font-bold text-3xl border-4 border-white/20 shadow-lg">
                        {(selectedCompany.companyName || selectedCompany.name)?.charAt(0)?.toUpperCase() || 'C'}
                      </div>
                    )}
                    {/* Verification badge */}
                    <div className="absolute -bottom-2 -right-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center border-3 border-white shadow-lg ${
                        selectedCompany.isVerified
                          ? 'bg-green-500'
                          : 'bg-yellow-500'
                      }`}>
                        {selectedCompany.isVerified ? (
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="text-white">
                    <h2 className="text-3xl font-bold mb-2">{selectedCompany.companyName || selectedCompany.name}</h2>
                    <div className="flex items-center gap-4 mb-3">
                      <span className={`px-4 py-2 rounded-full text-sm font-semibold backdrop-blur-sm border ${
                        selectedCompany.isVerified
                          ? 'bg-green-500/20 text-green-100 border-green-400/30'
                          : 'bg-yellow-500/20 text-yellow-100 border-yellow-400/30'
                      }`}>
                        {selectedCompany.isVerified ? '✓ VERIFIED COMPANY' : '⏳ PENDING VERIFICATION'}
                      </span>
                      <span className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-medium border border-white/20">
                        {selectedCompany.industry || 'Technology'}
                      </span>
                    </div>
                    <div className="flex items-center gap-6 text-white/80">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="text-sm">
                          {selectedCompany.city || selectedCompany.state || selectedCompany.country
                            ? `${selectedCompany.city || ''}, ${selectedCompany.state || ''}, ${selectedCompany.country || ''}`.replace(/^,\s*|,\s*$/g, '').replace(/,\s*,/g, ',')
                            : selectedCompany.location || 'Location not specified'
                          }
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <span className="text-sm">{selectedCompany.companySize || 'Size not specified'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6" />
                        </svg>
                        <span className="text-sm font-semibold">{selectedCompany.jobCount || 0} Active Jobs</span>
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => setShowCompanyModal(false)}
                  className="p-3 hover:bg-white/10 rounded-xl transition-colors text-white/80 hover:text-white"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content Area */}
            <div className="p-8 bg-gray-50/50 overflow-y-auto" style={{maxHeight: 'calc(95vh - 200px)'}}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-6">
                  {/* Company Description */}
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">Company Overview</h3>
                    </div>
                    <div className="prose max-w-none text-gray-700 leading-relaxed">
                      {typeof selectedCompany.description === 'string'
                        ? selectedCompany.description.split('\n').map((paragraph, index) => (
                            <p key={index} className="mb-4 text-gray-600">{paragraph}</p>
                          ))
                        : <p className="mb-4 text-gray-600">{selectedCompany.description || 'No description available'}</p>
                      }
                    </div>
                  </div>

                  {/* Benefits & Perks */}
                  {selectedCompany.benefits && selectedCompany.benefits.length > 0 && selectedCompany.benefits.some(b => b.trim()) && (
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                          </svg>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">Benefits & Perks</h3>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {Array.isArray(selectedCompany.benefits)
                          ? selectedCompany.benefits.filter(b => b && b.trim()).map((benefit, index) => (
                              <div key={index} className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                <span className="text-gray-700">{benefit}</span>
                              </div>
                            ))
                          : typeof selectedCompany.benefits === 'string'
                          ? selectedCompany.benefits.split('\n').map((benefit, index) => (
                              <div key={index} className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                <span className="text-gray-700">{benefit}</span>
                              </div>
                            ))
                          : <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                              <span className="text-gray-700">{selectedCompany.benefits}</span>
                            </div>
                        }
                      </div>
                    </div>
                  )}

                  {/* Mission, Vision, Culture */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {selectedCompany.mission && selectedCompany.mission.trim() && (
                      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                            <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                          </div>
                          <h4 className="text-lg font-bold text-gray-900">Mission</h4>
                        </div>
                        <p className="text-gray-600 leading-relaxed">{selectedCompany.mission}</p>
                      </div>
                    )}

                    {selectedCompany.vision && selectedCompany.vision.trim() && (
                      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                            <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </div>
                          <h4 className="text-lg font-bold text-gray-900">Vision</h4>
                        </div>
                        <p className="text-gray-600 leading-relaxed">{selectedCompany.vision}</p>
                      </div>
                    )}

                    {selectedCompany.culture && selectedCompany.culture.trim() && (
                      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                            <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                          </div>
                          <h4 className="text-lg font-bold text-gray-900">Culture</h4>
                        </div>
                        <p className="text-gray-600 leading-relaxed">{selectedCompany.culture}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Sidebar - Company Details */}
                <div>
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">Company Details</h3>
                    </div>

                    <div className="space-y-4">
                      {/* Industry */}
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <div className="flex items-center gap-2 mb-2">
                          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6" />
                          </svg>
                          <p className="text-sm font-semibold text-gray-700">Industry</p>
                        </div>
                        <p className="text-gray-900 font-medium">{selectedCompany.industry || 'Not specified'}</p>
                      </div>

                      {/* Location */}
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <div className="flex items-center gap-2 mb-2">
                          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <p className="text-sm font-semibold text-gray-700">Location</p>
                        </div>
                        <p className="text-gray-900 font-medium">
                          {selectedCompany.city || selectedCompany.state || selectedCompany.country
                            ? `${selectedCompany.city || ''}, ${selectedCompany.state || ''}, ${selectedCompany.country || ''}`.replace(/^,\s*|,\s*$/g, '').replace(/,\s*,/g, ',')
                            : selectedCompany.location || 'Not specified'
                          }
                        </p>
                      </div>

                      {/* Company Size */}
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <div className="flex items-center gap-2 mb-2">
                          <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          <p className="text-sm font-semibold text-gray-700">Company Size</p>
                        </div>
                        <p className="text-gray-900 font-medium">{selectedCompany.companySize || 'Not specified'}</p>
                      </div>

                      {/* Website */}
                      {selectedCompany.website && (
                        <div className="p-4 bg-gray-50 rounded-xl">
                          <div className="flex items-center gap-2 mb-2">
                            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                            </svg>
                            <p className="text-sm font-semibold text-gray-700">Website</p>
                          </div>
                          <a
                            href={selectedCompany.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 font-medium"
                          >
                            {selectedCompany.website}
                          </a>
                        </div>
                      )}

                      {/* Registration Date */}
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <div className="flex items-center gap-2 mb-2">
                          <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0h8m-8 0H6a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V9a2 2 0 00-2-2h-2m-8 0V7" />
                          </svg>
                          <p className="text-sm font-semibold text-gray-700">Registration Date</p>
                        </div>
                        <p className="text-gray-900 font-medium">{formatDate(selectedCompany.createdAt)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamCompanies;
